import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { WeatherActions } from './weather.actions';
import { catchError, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { WeatherService } from '../../../services/weather.service';
import { selectWeatherEntities } from './weather.selectors';
import { computeWeatherKey } from './weather.reducer';
import { Address } from '../../../warehouse-models';
import { WeatherInfo } from '../weather-info.models';

const CACHE_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

@Injectable()
export class WeatherEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private weatherService: WeatherService
  ) {}

  loadWeather$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WeatherActions.loadWeather),
      mergeMap(({ address }) => {
        const key = computeWeatherKey(address);
        return of(address).pipe(
          withLatestFrom(this.store.select(selectWeatherEntities)),
          mergeMap(([addr, entities]) => {
            const existing = entities[key];
            const now = Date.now();
            if (existing && existing.fetchedAt && now - existing.fetchedAt < CACHE_WINDOW_MS && existing.data) {
              // Serve from cache
              return of(WeatherActions.loadWeatherCached({ key, address }));
            }
            // Decide which service method to call (coords vs address)
            const hasCoords = !!addr.latitude && !!addr.longitude;
            const request$ = hasCoords
              ? this.weatherService.getWeather(addr.latitude!, addr.longitude!)
              : this.weatherService.getWeatherByAddress(addr);
            return request$.pipe(
              map((data: any) => {
                const weatherData = data as any as WeatherInfo;
                if (weatherData?.main) {
                  weatherData.main.temp = Math.round(weatherData.main.temp);
                  weatherData.main.feels_like = Math.round(weatherData.main.feels_like);
                }
                return WeatherActions.loadWeatherSuccess({
                  key,
                  address: addr as Address,
                  data: weatherData as WeatherInfo,
                  fetchedAt: Date.now(),
                });
              }),
              catchError((err) =>
                of(
                  WeatherActions.loadWeatherFailure({
                    key,
                    address: addr as Address,
                    error: err?.message || 'Failed to load weather',
                  })
                )
              )
            );
          })
        );
      })
    )
  );
}
